import * as PIXI from 'pixi.js';
import { StoryManager } from '@/systems';

export class DayDisplay {
  private container: PIXI.Container;
  private background: PIXI.Sprite;
  private fallbackBackground: PIXI.Graphics;
  private dayText: PIXI.Text;
  private nameText: PIXI.Text;
  private descriptionText: PIXI.Text;
  private storyManager: StoryManager;

  constructor() {
    this.storyManager = StoryManager.getInstance();
    this.container = new PIXI.Container();

    // Create fallback background first to ensure visibility
    this.fallbackBackground = new PIXI.Graphics();
    this.fallbackBackground.beginFill(0x000000, 0.8);
    this.fallbackBackground.drawRoundedRect(0, 0, 450, 150, 10);
    this.fallbackBackground.endFill();

    // Create placeholder sprite initially
    this.background = new PIXI.Sprite(PIXI.Texture.EMPTY);
    this.background.width = 450;
    this.background.height = 150;

    // Create day number text
    this.dayText = new PIXI.Text('Day 1', {
      fontFamily: 'Serif',
      fontSize: 24,
      fill: 0xffffff,
      fontWeight: 'bold',
      align: 'right',
    });
    this.dayText.anchor.set(1, 0); // Right aligned, top aligned
    this.dayText.x = 400;
    this.dayText.y = 40; // 40px top padding + 10px

    // Create day name text
    // this.nameText = new PIXI.Text('First Contact', {
    //   fontFamily: 'Arial',
    //   fontSize: 16,
    //   fill: 0xffdd44,
    //   fontWeight: 'bold',
    //   align: 'right',
    // });
    // this.nameText.anchor.set(1, 0); // Right aligned, top aligned
    // this.nameText.x = 400
    // this.nameText.y = 75; // 40px top padding + 40px for day text

    // Create description text
    this.descriptionText = new PIXI.Text('', {
      fontFamily: 'Serif',
      fontSize: 16,
      fill: 0xcccccc,
      wordWrap: true,
      wordWrapWidth: 270,
      align: 'right',
    });
    this.descriptionText.anchor.set(1, 0); // Right aligned, top aligned
    this.descriptionText.x = 400;
    this.descriptionText.y = 75; // 40px top padding + 65px for previous text

    // Add fallback first, then sprite
    this.container.addChild(this.fallbackBackground);
    this.container.addChild(this.background);
    this.container.addChild(this.dayText);
    // this.container.addChild(this.nameText);
    this.container.addChild(this.descriptionText);
    
    // Ensure visibility for debugging
    this.container.visible = true;
    console.log('DayDisplay initialized with container size:', this.container.width, 'x', this.container.height);

    // Initial position will be updated by updatePosition
    this.container.x = 0;
    this.container.y = 0; // Remove spacing from top edge

    // Listen for day changes
    this.storyManager.addDayEventListener('dayChanged', () => {
      this.updateDisplay();
    });

    // Load the background texture after initialization
    this.initializeBackgroundTexture();

    // Initial update
    this.updateDisplay();
  }

  private async initializeBackgroundTexture(): Promise<void> {
    try {
      const texture = await PIXI.Assets.load('assets/misc/day-indicator.png');
      if (texture) {
        this.background.texture = texture;
        this.background.visible = true;
        this.fallbackBackground.visible = false;
        console.log('Day indicator texture loaded successfully');
      }
    } catch (error) {
      console.warn('Failed to load day-indicator.png, using fallback background:', error);
      this.background.visible = false;
      this.fallbackBackground.visible = true;
    }
  }

  private updateDisplay(): void {
    const currentDay = this.storyManager.getCurrentDay();
    const dayInfo = this.storyManager.getDayInfo(currentDay);

    if (dayInfo) {
      if(dayInfo.dayString){
        this.dayText.text = dayInfo.dayString;
      }else{
        this.dayText.text = `Day ${currentDay}`;
      }
      // this.nameText.text = dayInfo.name;
      this.descriptionText.text = dayInfo.description || '';
    } else {
      this.dayText.text = `Day ${currentDay}`;
      // this.nameText.text = 'Unknown';
      this.descriptionText.text = '';
    }

    // Background size will be determined by the image asset
    // The text content will adapt to the background image dimensions
  }

  public updatePosition(screenWidth: number, _screenHeight: number): void {
    // Dynamic scaling based on reference resolution (e.g. 1280x720)
    // On mobile landscape (812x375) smaller UI
    const referenceWidth = 1280;
    const minScale = 0.5;
    const maxScale = 1.0;
    
    let scale = screenWidth / referenceWidth;
    scale = Math.max(minScale, Math.min(maxScale, scale));
    
    this.container.scale.set(scale);

    // Remove safe padding 
    const topPadding = 0; 
    const rightPadding = 0;

    const bgWidth = this.background.texture?.source ? this.background.width : 300;
    const displayWidth = bgWidth * scale;
    
    this.container.x = screenWidth - displayWidth - rightPadding;
    this.container.y = topPadding;
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  public destroy(): void {
    this.container.destroy();
  }
}
